<template>
  <div class="mc-fleet-task-instant">
    <McLoading :isLoading="isTransitionLoading" />
    <McTable
      v-show="isTableDataAvailable"
      ref="mc-table-fti"
      :key="tableKey"
      :fields="computedFields"
      :apiClient="apiClient"
      :activeFilters="activeFilters"
      :hasSelectAll="enableSelectAll"
      :hasDelete="false"
      :bulkDeleteApi="true"
      :searchable="true"
      :customHandlers="customHandlers"
      :hasSelectableRow="false"
      :initialPage="initialParams.page"
      :initialLimit="initialParams.limit"
      :initialSearch="initialParams.search"
      :withCard="withCard"
      hasEmit:deleteSuccess
      stickyHeader
      @deleteSuccess="handleDeleteSuccess"
      @available-change="handleDataAvailable"
      @handleSelectValue="handleClickCheckbox"
    >
      <template #before-table="{ isSelected, showDeleteModal }">
        <div class="d-flex justify-content-between mb-3">
          <McInlineFilter
            :filterItems="inlineFilterItems"
            :activeKeys="activeFilters['status.name']"
            @update:activeKeys="handleChangeActiveFilters([{ key: 'status.name', values: $event }])"
          />

          <div class="d-flex gap-2">
            <McTableFilter
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              @submit="handleChangeActiveFilters"
            >
              <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
                <InputTableFilter
                  :formValues="formValues"
                  :fullObjects="fullObjects"
                  @update:formValues="updateFormValues($event)"
                  @update:fullObjects="updateFullObjects($event)"
                />
              </template>
            </McTableFilter>

            <ActionDropdown
              :disabled="!isSelected"
              :actionHandlers="{
                handleDuplicate,
                handleDelete: showDeleteModal,
                handleTransitionToScheduled,
                handleTransitionToStarted,
                handleTransitionToEnded,
                handleTransitionToCanceled,
              }"
            />
          </div>
        </div>
      </template>

      <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataFields"
          @setColumn="handleSetDataFields"
        />
      </template>

      <template #head(note)="{ label }">
        <div class="__header-note">
          <span>{{ label }}</span>
          <Tooltip name="note-input" />
        </div>
      </template>

      <!-- Driver -->
      <template #[`cell(driver.name)`]="{ value }">
        {{ value || '-' }}
      </template>

      <!-- Created On -->
      <template #cell(created_on)="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>

      <!-- Target time departure -->
      <template #cell(expected_started_on)="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>
      <!-- Actual started on -->
      <template #cell(actual_started_on)="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else> - </template>
      </template>
      <!-- Target time departure -->
      <template #cell(target_time_departure)="{ value }">
        <div>{{ formatIndexDate(value).date }}</div>
        <div class="mc-datetime">
          {{ formatIndexDate(value).time }}
        </div>
      </template>
      <!-- timeline route -->
      <template #[`cell(timeline_route)`]="{ value, item }">
        <div v-if="!value?.length">-</div>

        <div v-else-if="hasAutoEndedQuickAssignment" class="__timeline-route">
          <div class="__timeline-route-counter">
            {{ item?.total_current_point }}/{{ item?.total_point }}
          </div>

          <div class="__timeline-route-body">
            <button
              v-if="shouldShowTimelinePagination(value)"
              type="button"
              class="__timeline-route-nav"
              :aria-label="$t('global.prev')"
              :disabled="!canGoToPreviousTimelinePage(item.id, value)"
              @click.stop="handleTimelinePageChange(item.id, value, 'prev')"
            >
              <i class="mc-icon icon-outline_chevron-left" />
            </button>

            <div class="__timeline-route-points">
              <div
                v-for="point in getVisibleTimelinePoints(item.id, value)"
                :key="'timeline-point-' + point.id"
                class="__timeline-route-point"
              >
                <FleetTooltip :point="point" />
                <div
                  :id="'timeline-point-' + point.id"
                  class="__point-line"
                  :style="{ backgroundColor: point.visit_status?.background_color || '#E3E7EE' }"
                />
              </div>
            </div>

            <button
              v-if="shouldShowTimelinePagination(value)"
              type="button"
              class="__timeline-route-nav"
              :aria-label="$t('global.next')"
              :disabled="!canGoToNextTimelinePage(item.id, value)"
              @click.stop="handleTimelinePageChange(item.id, value, 'next')"
            >
              <i class="mc-icon icon-outline_chevron-right" />
            </button>
          </div>
        </div>

        <div v-else class="d-flex w-100">
          <template v-for="point in value">
            <FleetTooltip :key="'tooltip-' + point.id" :point="point" />
            <div
              :id="'timeline-point-' + point.id"
              :key="'point-' + point.id"
              class="__point-line __point-line--legacy"
              :style="{ backgroundColor: point.visit_status?.background_color || '#E3E7EE' }"
            />
          </template>
        </div>
      </template>
      <!-- status -->
      <template #[`cell(status.name)`]="{ item }">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <span
              v-if="item.status?.color"
              class="__status-dots"
              :style="{ backgroundColor: item.status.color }"
            ></span>
            <span class="mc-text-status">{{ computedStatus(item) }}</span>
          </div>

          <McIconTooltip
            v-if="hasAutoEndedQuickAssignment && shouldShowAutomationTooltip(item)"
            :id="`automation-tooltip-${item.id}`"
            icon="icon-fill_flash"
            :icon-size="14"
            :tooltip-title="$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.title')"
            :tooltip-items="getAutomationTooltipItems(item)"
            variant="multi-info"
            placement="bottomright"
          />
        </div>
      </template>
      <!-- current information -->
      <template #[`cell(current_point_status)`]="{ value }">
        <div v-if="emptyCurrentStatus(value)">-</div>
        <template v-else>
          <div v-if="value['estimated_arrival_on']">
            <strong>{{ $t('fleetTaskInstant.text.estimatedArrival') }}</strong> -
            {{ formatIndexDate(value['estimated_arrival_on']).time }}
          </div>
          <div v-if="value['actual_arrival_on']">
            <strong>{{ $t('fleetTaskInstant.text.actualArrival') }}</strong> -
            {{ formatIndexDate(value['actual_arrival_on']).time }}
          </div>
          <div v-if="value['estimated_departure_on']">
            <strong>{{ $t('fleetTaskInstant.text.estimatedDeparture') }}</strong> -
            {{ formatIndexDate(value['estimated_departure_on']).time }}
          </div>
          <div v-if="value['actual_departure_on']">
            <strong>{{ $t('fleetTaskInstant.text.actualDeparture') }}</strong> -
            {{ formatIndexDate(value['actual_departure_on']).time }}
          </div>
        </template>
      </template>
      <!-- current information -->
      <template #[`cell(current_point_location)`]="{ value }">
        <div v-if="value.address.full_name === null && value.address.name === null">-</div>
        <div v-else class="text-truncate">
          <strong>{{
            value.status.raw_type === 'VISITING'
              ? $t('fleetTaskInstant.text.in')
              : $t('fleetTaskInstant.text.to')
          }}</strong>
          {{ value.address.name || value.address.full_name }}
        </div>
      </template>

      <!-- Note -->
      <template #cell(note)="{ value }">
        {{ value || '-' }}
      </template>

      <!-- Phone Number -->
      <template #cell(phone_number)="{ value }">
        {{ value || '-' }}
      </template>

      <!-- Custom Fields -->
      <template
        v-for="(field, index) in customFields"
        #[`cell(custom_field.${field.key})`]="{ value }"
      >
        <template v-if="field.type === 'TIMESTAMPTZ'">
          <div :key="`div1-${index}`">
            {{ formatIndexDate(value).date }}
          </div>
          <div :key="`div2-${index}`" class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
        <template v-else-if="field.type === 'NUMBER'">
          {{ value.toLocaleString() || '-' }}
        </template>
        <template v-else>
          {{ value || '-' }}
        </template>
      </template>

      <!-- Track FO -->
      <template #cell(track_fo)="data">
        <ShareTrackButton
          :rawStatus="data.item.status.raw_type"
          @click="shareTrackBtnHandler(data)"
        />
      </template>
    </McTable>

    <b-card v-show="!isTableDataAvailable" class="mt-3">
      <div class="empty-table-container">
        <img :src="illustrationPath" alt />
        <strong>{{ $t('fleetTaskInstant.text.emptyFleetTaskInstant') }}</strong>
        <p>{{ $t('fleetTaskInstant.text.scheduleNewFleetTaskInstant') }}</p>
        <McButton
          variant="primary"
          :text="$t('global.addField')"
          icon="icon-outline_plus"
          @click="handleAdd"
        />
      </div>
    </b-card>

    <ShareTrackModal
      modalName="share-track-modal"
      :number="trackedFOData?.number"
      :trackLink="trackedFOData?.track_link"
    />

    <!-- Transition to SCHEDULED -->
    <DefaultTransitionModal
      ref="mc-modal-scheduled"
      :textSecondaryBtn="$t('global.cancel')"
      :textPrimaryBtn="$t('fleetTaskInstant.confirmModal.transitionToScheduled.confirmBtn')"
      :entityData="{
        status: {
          name: uniqueItemStatus[0] || '',
          label: uniqueItemStatus[0] ? statusNameToLabelMap[uniqueItemStatus[0]] : '',
        },
      }"
      :newStatusTransition="{ status: status.SCHEDULED }"
      :hideStatusPreview="uniqueItemStatus.length > 1"
      @transition="handleBulkTransition"
    >
      <template #title>
        <span class="transition-modal-title">{{
          $t('fleetTaskInstant.confirmModal.transitionToScheduled.title')
        }}</span>
      </template>
      <template #confirmationText>
        <p
          v-html="
            $t('fleetTaskInstant.confirmModal.transitionToScheduled.content', {
              total: selectedItems.length,
            })
          "
        />
      </template>
    </DefaultTransitionModal>

    <!-- Transition to STARTED -->
    <DefaultTransitionModal
      ref="mc-modal-started"
      :textSecondaryBtn="$t('global.cancel')"
      :textPrimaryBtn="$t('fleetTaskInstant.confirmModal.transitionToStarted.confirmBtn')"
      :entityData="{
        status: {
          name: uniqueItemStatus[0] || '',
          label: uniqueItemStatus[0] ? statusNameToLabelMap[uniqueItemStatus[0]] : '',
        },
      }"
      :newStatusTransition="{ status: status.STARTED }"
      :hideStatusPreview="uniqueItemStatus.length > 1"
      @transition="handleBulkTransition"
    >
      <template #title>
        <span class="transition-modal-title">{{
          $t('fleetTaskInstant.confirmModal.transitionToStarted.title')
        }}</span>
      </template>
      <template #confirmationText>
        <p
          v-html="
            $t('fleetTaskInstant.confirmModal.transitionToStarted.content', {
              total: selectedItems.length,
            })
          "
        />
      </template>
    </DefaultTransitionModal>

    <!-- Transition to ENDED -->
    <DefaultTransitionModal
      ref="mc-modal-ended"
      :textSecondaryBtn="$t('global.cancel')"
      :textPrimaryBtn="$t('fleetTaskInstant.confirmModal.transitionToEnded.confirmBtn')"
      :entityData="{
        status: {
          name: uniqueItemStatus[0] || '',
          label: uniqueItemStatus[0] ? statusNameToLabelMap[uniqueItemStatus[0]] : '',
        },
      }"
      :newStatusTransition="{ status: status.ENDED }"
      :hideStatusPreview="uniqueItemStatus.length > 1"
      @transition="handleBulkTransition"
    >
      <template #title>
        <span class="transition-modal-title">{{
          $t('fleetTaskInstant.confirmModal.transitionToEnded.title')
        }}</span>
      </template>
      <template #confirmationText>
        <div>
          <McAlert withIcon variant="warning" class="mb-2">
            <template #label>
              <span v-html="$t('fleetTaskInstant.confirmModal.transitionToEnded.alertText')" />
            </template>
          </McAlert>

          <p
            v-html="
              $t('fleetTaskInstant.confirmModal.transitionToEnded.content', {
                total: selectedItems.length,
              })
            "
          />
        </div>
      </template>
    </DefaultTransitionModal>

    <!-- Transition to CANCELED -->
    <TransitionToCancelModal
      ref="mc-modal-canceled"
      :entityData="{
        status: {
          name: uniqueItemStatus[0] || '',
          label: uniqueItemStatus[0] ? statusNameToLabelMap[uniqueItemStatus[0]] : '',
        },
      }"
      :newStatusTransition="{ status: status.CANCELED }"
      :selectedItems="selectedItems"
      @transition="handleBulkTransition"
    />

    <!-- Loading Bulk Action -->
    <ModalValueHelp
      ref="mc-modal-user-group"
      :context="userGroupContext"
      :attrs="userGroupAttrs"
      @update:fullObject="handleUserGroupSelect"
    />
  </div>
</template>

<script>
import { MixPanel } from '@/mixin/mixpanel'
import appConfig from '@/app.config'

import McTable from '@/components/McTable'
import McInlineFilter from '@/components/McInlineFilter'
import McTableFilter from '@/components/McTableFilter'
import DefaultTransitionModal from '@/components/McTransitionStatus/DefaultTransitionModal.vue'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McFilterSelect from '@/components/McFilterSelect'
import ModalValueHelp from '@/components/formulate/ValueHelpInputV2/components/ModalValueHelp'

import InputTableFilter from './components/InputTableFilter'
import FleetTooltip from './components/FleetTooltip'
import filterMappings from './lib/constants/filter-mappings'
import ShareTrackModal from './components/ShareTrackModal'
import ShareTrackButton from './components/ShareTrackButton'
import ActionDropdown from './components/ActionDropdown'
import TransitionToCancelModal from './components/TransitionToCancelModal'
import Tooltip from './components/Tooltip'
import McIconTooltip from '@/components/McIconTooltip'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import * as FleetTaskInstantBff from '@/services/rest-api/lib/backend-for-frontend/fleet-task-instant/fleet-task-instant'
import * as CustomField from '@/services/rest-api/lib/setting/quick-assignment/custom-field'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

import fields from './lib/fields/listTableFields'
import inlineFilterItems from './lib/constants/inline-filter-items'
import parseFilterDataMappings from './lib/constants/parse-filter-data-mappings'
import formatFilterMappings from './lib/constants/format-filter-mappings'
import status, {
  statusIdToLabelMap,
  statusNameToIdMap,
  statusNameToLabelMap,
  statusLabelToNameMap,
  successToastMsg,
  failedToastMsg,
} from './lib/constants/status.js'

import { formatIndexDate } from '@/helpers/tzDate'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'

import isEqual from 'lodash/isEqual'
import difference from 'lodash/difference'
import cloneDeep from 'lodash/cloneDeep'

import moment from 'moment'
import { errorMsg, successMsg } from './lib/constants/toast-msg'
moment.locale('id')

const TIMELINE_ROUTE_PAGE_SIZE = 10

export default {
  page: {
    title: 'Quick Assignment',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    McTable,
    McInlineFilter,
    McTableFilter,
    DefaultTransitionModal,
    McButton,
    McAlert,
    McFilterSelect,
    FleetTooltip,
    InputTableFilter,
    ShareTrackModal,
    ShareTrackButton,
    ActionDropdown,
    TransitionToCancelModal,
    Tooltip,
    ModalValueHelp,
    McIconTooltip,
  },
  mixins: [MixPanel, fields, Helper],
  props: {
    withCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeFilters: {},
      initialParams: {},
      params: {},
      selectedItems: [],
      customFields: [],

      tableKey: 1,

      trackedFOData: null,
      metadata: null,
      isTableDataAvailable: true,
      timelineRoutePageMap: {},

      status,
      statusIdToLabelMap,
      statusNameToLabelMap,

      userGroupContext: {
        label: 'User Group',
        model: {},
        blurHandler: () => {},
      },
      skipValidationUserGroup: false,
      isTransitionLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      isWhitelabel: 'identity/isWhitelabel',
      isMetrack: 'identity/isMetrack',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    apiClient() {
      let getFleetTaskInstant = FleetTaskInstant.get(
        null,
        this.handleSetParams,
        this.handleSetMetadata
      )

      if (Object.keys(this.activeFilters).length > 0) {
        const queryParamFilters = []

        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          queryParamFilters.push(filter)

          getFleetTaskInstant = FleetTaskInstant.get(
            queryParamFilters,
            this.handleSetParams,
            this.handleSetMetadata
          )
        })
      }

      return {
        get: getFleetTaskInstant,
        delete: FleetTaskInstant.deleteMany,
      }
    },
    computedFields() {
      let baseFields = this.fields

      const hasUserGroupAccess = this.hasAccessAll(['tms-user-group:read'])
      const userGroupField = baseFields.find((field) => field.key === 'user_group.name')
      if (userGroupField) {
        userGroupField.isShow = hasUserGroupAccess
      }

      baseFields = baseFields.filter((field) => field.isShow !== false)

      if (this.customFields.length === 0) {
        return baseFields
      }

      const newFields = cloneDeep(baseFields)

      this.customFields.forEach((field) => {
        const fieldsIndex = newFields.length - 1
        newFields.splice(fieldsIndex, 0, {
          label: field.name,
          key: `custom_field.${field.key}`,
          type: field.type,
          tdClass: 'text-truncate',
          tdAttr: {
            style: 'min-width:248px;max-width:248px',
          },
          thAttr: {
            style: 'min-width:248px;max-width:248px',
          },
        })
      })

      return newFields
    },
    inlineFilterItems() {
      return inlineFilterItems(this)
    },
    customHandlers() {
      return {
        handleClickRow: this.handleClickRow,
        handleOpenNewTab: this.handleOpenNewTab,
      }
    },
    illustrationPath() {
      if (this.isWhitelabel() || this.isMetrack()) {
        return require(`@/assets/images/illustration/whitelabel/no-data.svg`)
      }
      return require(`@/assets/images/illustration/saas/no-data.svg`)
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    enableSelectAll() {
      // Disable select all for 'Semua' filter
      return this.activeFilters['status.name']?.length > 0
    },
    uniqueItemStatus() {
      const uniqueStatus = []
      for (const item of this.selectedItems) {
        const status = item?.status?.name
        if (status === undefined) continue

        if (!uniqueStatus.includes(status)) {
          uniqueStatus.push(status)
        }
      }

      return uniqueStatus
    },
    computedStatus() {
      return (item) => {
        const mappedStatus = this.statusIdToLabelMap[item.status?.raw_type]

        if (mappedStatus) {
          return this.$t(mappedStatus)
        }

        return item.status?.name
      }
    },
    userGroupAttrs() {
      return {
        name: 'user_group',
        modalTitle: `${this.$t('global.form.select')} ${this.$t('global.field.userGroup')}`,
        trackBy: 'id',
        tableFields: [
          {
            label: this.$t('global.field.userGroup'),
            key: 'name',
            isSortable: false,
          },
          {
            label: this.$t('global.field.vehicleGroup'),
            key: 'vgroups',
            isSortable: false,
            formatter: (value) => {
              return value?.length != null
                ? value.length === 0
                  ? this.$t('global.text.allVehicleGroup')
                  : value.length
                : '-'
            },
          },
        ],
        listGetter: async function (opts) {
          let result = {}
          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_USER_GROUP,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          const ugroups = res.data.ugroups.ugroups

          result = {
            metadata: {
              total_count: ugroups.pagination.total,
              page: ugroups.pagination.page,
            },
            data: {
              paginated_result: ugroups.data,
            },
          }

          return result
        },
        isMandatory: true,
      }
    },
    hasTmsUserGroupAccess() {
      return (
        this.hasAccessAll([
          'tms-user-group:create',
          'tms-user-group:delete',
          'tms-user-group:read',
          'tms-user-group:update',
        ]) && this.isSuperUser()
      )
    },
    isBulkTransitionV2() {
      return this.getFeatureFlagValue('setTransitionQAandProductFlag')
    },
    hasAutoEndedQuickAssignment() {
      const data = this.getFeatureFlagValue('customFeature.UnfinishedQA')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
  },
  created() {
    this.setFilterFromURL()

    // get custom fields
    this.getCustomFields()
  },
  methods: {
    formatIndexDate,

    shouldShowAutomationTooltip(item) {
      return this.getAutomationTooltipItems(item).length > 0
    },

    getAutomationTooltipItems(item) {
      const isManualEnded = ['MANUAL'].includes(item?.ended_by) && item?.auto_end_type

      return [
        {
          label: this.isItemAutoStart(item)
            ? this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.autoRun')
            : this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.run'),
          value: this.getAutomationRunText(item),
        },
        ...(!isManualEnded
          ? [
              {
                label:
                  this.isAutomationEnded(item) &&
                  ['ALL_VISITED', 'LAST_POINT', 'EXPIRED_DURATION'].includes(item?.ended_by)
                    ? this.$t(
                        'fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.autoComplete'
                      )
                    : this.$t(
                        'fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.complete'
                      ),
                value: this.getAutomationCompleteText(item),
              },
            ]
          : []),
        {
          label: this.$t('fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.activeLimit'),
          value: this.getAutomationActiveLimitText(item),
        },
      ].filter((tooltipItem) => tooltipItem.value)
    },

    getAutomationRunText(item) {
      if (!item?.auto_start_type) {
        return ''
      }

      if (this.isAutomationStartedOrEnded(item) && !this.isItemAutoStart(item)) {
        return ''
      }

      if (this.isAutomationStartedOrEnded(item) && this.isItemAutoStart(item)) {
        return this.formatAutomationDateTime(item?.actual_departure_on || item?.actual_started_on)
      }

      return this.isItemStartPoint(item)
        ? this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenDepartingFromStartingPoint')
        : this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenArrivingAtStartingPoint')
    },

    getAutomationCompleteText(item) {
      if (!item?.auto_end_type) {
        return ''
      }

      if (this.isAutomationEnded(item)) {
        return this.formatAutomationDateTime(item?.actual_arrival_on)
      }

      if (item?.auto_end_type === 'ALL_VISITED' && !this.isItemEndPoint(item)) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenAllLocationsVisited')
      }

      if (item?.auto_end_type === 'ALL_VISITED' && this.isItemEndPoint(item)) {
        return this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.whenArrivingAtEndingPointAndAllLocationsVisited'
        )
      }

      if (item?.auto_end_type === 'LAST_POINT' && !this.isItemEndPoint(item)) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenLeavingLastLocation')
      }

      if (item?.auto_end_type === 'LAST_POINT' && this.isItemEndPoint(item)) {
        return this.$t('fleetTaskInstant.autoEndedQuickAssigment.whenArrivingAtEndingPoint')
      }

      return ''
    },

    getAutomationActiveLimitText(item) {
      const duration = item?.auto_end_by_duration ?? item?.auto_ended_duration
      if (!duration || this.isAutomationEnded(item)) {
        return ''
      }

      if (item?.status?.raw_type === 'STARTED' && item?.auto_end_duration_remaining) {
        return this.formatAutomationDurationText(item?.auto_end_duration_remaining, true)
      }

      return this.formatAutomationDurationText(duration)
    },

    isAutomationStartedOrEnded(item) {
      return ['STARTED', 'ENDED'].includes(item?.status?.raw_type)
    },

    isAutomationEnded(item) {
      return item?.status?.raw_type === 'ENDED'
    },

    isItemStartPoint(item) {
      const points = item?.timeline_route || []
      return points[0]?.point_type === 'START'
    },

    isItemEndPoint(item) {
      const points = item?.timeline_route || []
      return points[points.length - 1]?.point_type === 'END'
    },

    isItemAutoStart(item) {
      return item?.started_by === 'AUTOMATION'
    },

    getTimelineTotalPages(points = []) {
      return Math.max(Math.ceil(points.length / TIMELINE_ROUTE_PAGE_SIZE), 1)
    },

    getTimelineCurrentPage(itemId, points = []) {
      const savedPage = this.timelineRoutePageMap[itemId] || 0
      return Math.min(savedPage, this.getTimelineTotalPages(points) - 1)
    },

    getVisibleTimelinePoints(itemId, points = []) {
      const currentPage = this.getTimelineCurrentPage(itemId, points)
      const startIndex = currentPage * TIMELINE_ROUTE_PAGE_SIZE
      return points.slice(startIndex, startIndex + TIMELINE_ROUTE_PAGE_SIZE)
    },

    shouldShowTimelinePagination(points = []) {
      return points.length > TIMELINE_ROUTE_PAGE_SIZE
    },

    canGoToPreviousTimelinePage(itemId, points = []) {
      return this.getTimelineCurrentPage(itemId, points) > 0
    },

    canGoToNextTimelinePage(itemId, points = []) {
      return this.getTimelineCurrentPage(itemId, points) < this.getTimelineTotalPages(points) - 1
    },

    handleTimelinePageChange(itemId, points = [], direction) {
      const currentPage = this.getTimelineCurrentPage(itemId, points)
      const lastPage = this.getTimelineTotalPages(points) - 1
      const nextPage =
        direction === 'next' ? Math.min(currentPage + 1, lastPage) : Math.max(currentPage - 1, 0)

      this.$set(this.timelineRoutePageMap, itemId, nextPage)
    },

    getTimelineProgressCount(points = []) {
      return points.filter(this.isTimelinePointCompleted).length
    },

    isTimelinePointCompleted(point) {
      const pendingStatuses = ['WILL_VISITED', 'NOT_VISITED', 'VISITING']
      return (
        !!point?.visit_status?.raw_type && !pendingStatuses.includes(point.visit_status.raw_type)
      )
    },

    formatAutomationDateTime(value) {
      if (!value) {
        return ''
      }

      const formattedDate = this.formatIndexDate(value)
      return `${formattedDate.date} ${formattedDate.time}`
    },

    formatAutomationDurationText(duration, isRemaining = false) {
      const parsedDuration = this.parseAutomationDuration(duration)
      const formattedValue = this.buildAutomationDurationLabel(parsedDuration)

      if (!formattedValue) {
        return ''
      }

      if (isRemaining) {
        return this.$t(
          'fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.remainingValue',
          { value: formattedValue }
        )
      }

      return formattedValue
    },

    buildAutomationDurationLabel({ days = 0, hours = 0, minutes = 0 }) {
      if (days > 0) {
        const labels = [this.translateAutomationDurationUnit('day', days)]

        if (hours > 0) {
          labels.push(this.translateAutomationDurationUnit('hour', hours))
        }

        return labels.join(' ')
      }

      if (hours > 0) {
        return this.translateAutomationDurationUnit('hour', hours)
      }

      return this.translateAutomationDurationUnit('minute', minutes)
    },

    translateAutomationDurationUnit(unit, value) {
      return this.$t(`fleetTaskInstant.autoEndedQuickAssigment.automationTooltip.${unit}`, {
        value,
      })
    },

    parseAutomationDuration(duration) {
      if (typeof duration !== 'string') {
        return { days: 7, hours: 0, minutes: 0 }
      }

      const matched = duration.match(
        /^P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/
      )

      if (!matched) {
        return { days: 7, hours: 0, minutes: 0 }
      }

      const weeks = Number(matched[1] || 0)
      const days = Number(matched[2] || 0) + weeks * 7
      const hours = Number(matched[3] || 0)
      const minutes = Number(matched[4] || 0)
      const seconds = Number(matched[5] || 0)
      let normalizedDays = days
      let normalizedHours = hours
      let normalizedMinutes = minutes

      if (seconds > 0) {
        normalizedMinutes += 1
      }

      if (normalizedMinutes >= 60) {
        normalizedHours += Math.floor(normalizedMinutes / 60)
        normalizedMinutes %= 60
      }

      if (normalizedHours >= 24) {
        normalizedDays += Math.floor(normalizedHours / 24)
        normalizedHours %= 24
      }

      if (normalizedDays > 0) {
        if (normalizedMinutes > 0) {
          normalizedHours += 1
          normalizedMinutes = 0
        }

        if (normalizedHours >= 24) {
          normalizedDays += Math.floor(normalizedHours / 24)
          normalizedHours %= 24
        }

        return {
          days: normalizedDays,
          hours: normalizedHours,
          minutes: 0,
        }
      }

      if (normalizedHours > 0) {
        if (normalizedMinutes > 0) {
          normalizedHours += 1
          normalizedMinutes = 0
        }

        if (normalizedHours >= 24) {
          return { days: 1, hours: 0, minutes: 0 }
        }

        return {
          days: 0,
          hours: normalizedHours,
          minutes: 0,
        }
      }

      if (normalizedMinutes >= 55) {
        return { days: 0, hours: 1, minutes: 0 }
      }

      return {
        days: 0,
        hours: 0,
        minutes: normalizedMinutes,
      }
    },

    handleClickRow(item) {
      const query = this.$route.query
      this.$router.push({ name: 'Fleet Task Instant Detail', params: { id: item.id }, query })
    },

    handleOpenNewTab(item) {
      const query = this.$route.query

      return this.$router.resolve({
        name: 'Fleet Task Instant Detail',
        params: { id: item.id },
        query,
      }).href
    },

    handleAdd() {
      this.$router.push({ name: 'Fleet Task Instant Create' })
    },

    handleDataAvailable(isAvailable) {
      if (Object.keys(this.activeFilters).length > 0) {
        this.isTableDataAvailable = true
        return
      }

      this.isTableDataAvailable = isAvailable
    },

    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)

        if (filter.values === null || filter.values === undefined || filter.values === '') return
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        this.$set(this.activeFilters, filter.key, filter.values)
      })

      // reset selected items if apply filters
      this.resetSelectedItems()
    },

    handleSetParams(params) {
      this.params = params
      this.setURLParams(params)
    },

    handleSetMetadata(metadata) {
      this.metadata = metadata
    },

    setURLParams(params) {
      const newParams = { type: 'instant' }

      // loop the params to set the newParams
      for (const [key, val] of Object.entries(params)) {
        // skip the undefined value
        if (val == undefined) continue

        // immediately set the value if it's not an Array
        if (!Array.isArray(val)) {
          newParams[key] = typeof val === 'string' ? val : val.toString()

          continue
        }

        // value with Array type usually is filters/sorts, so we need
        // to iterate it and set to each key
        val.forEach((item, index) => {
          newParams[key + index] = item
        })
      }

      // check if the URL has same values with new params
      const isSameParams = isEqual(newParams, this.$route.query)
      if (isSameParams) return

      // redirect to new query params
      const urlPath = '/fleet-task?' + new URLSearchParams(newParams).toString()
      this.$router.replace(urlPath)
    },

    // change filters from current URL
    setFilterFromURL() {
      const activeFilters = new Map()
      for (const [key, val] of Object.entries(this.$route.query)) {
        // set initial params exclude filters (page, limit, search)
        if (!key.includes('filters')) {
          let value
          if (key === 'search') {
            value = val || '-'
          } else if (key === 'limit') {
            value = ['10', '25', '50', '100'].includes(val) ? Number(val) || 1 : 10
          } else if (key === 'page') {
            value = Number(val) || 1
          }

          this.$set(this.initialParams, key, value)
          continue
        }

        const field = val.split(' ')[0]

        // TODO: Set filters from table filter
        if (field !== 'status.name') continue

        const { op, value } = formatFilterMappings[field](val)
        let newKey = `${field}:${op}`

        if (field === 'status.name') {
          newKey = field
        }

        // set new key in active filters
        activeFilters.set(newKey, value)
      }

      // if there are active filters, set the new values
      if (activeFilters.size == 0) return

      this.activeFilters = Object.fromEntries(activeFilters)
    },

    async getCustomFields() {
      if (this.getAccessRightQaOnDemand()) return
      const res = await CustomField.get({
        limit: Number.MAX_SAFE_INTEGER,
        page: 1,
        filters: ['entity eq FLEET_TASK_INSTANT', 'is_active eq true'],
        sorts: ['created_on asc'],
      })

      if (res.errors) return
      if (res.data.paginated_result.length === 0) return

      this.customFields = res.data.paginated_result
    },

    emptyCurrentStatus(currentStatus) {
      return (
        currentStatus['estimated_arrival_on'] === null &&
        currentStatus['actual_arrival_on'] === null &&
        currentStatus['estimated_departure_on'] === null &&
        currentStatus['actual_departure_on'] === null
      )
    },

    shareTrackBtnHandler(data) {
      this.trackedFOData = data.item
      this.$bvModal.show('share-track-modal')
    },

    handleClickCheckbox(ids) {
      if (Object.keys(ids).length === 0) {
        this.selectedItems = []
        return
      }

      const items = this.$refs['mc-table-fti'].items
      for (const [key, val] of Object.entries(ids)) {
        const item = items.find((v) => v.id === key)
        const index = this.selectedItems.findIndex((v) => v?.id === key)

        if (!val) {
          if (index === -1) continue

          this.selectedItems.splice(index, 1)
          continue
        }

        if (index !== -1) continue

        let newItem = item

        if (item === undefined) {
          newItem = {
            id: key,
          }
        }

        this.selectedItems.push(newItem)
      }
    },

    resetSelectedItems() {
      this.userGroupContext.model = {}
      this.skipValidationUserGroup = false
      this.selectedItems = []
      this.$refs['mc-table-fti'].ResetSelects()
    },

    async handleDuplicate() {
      const selectedIds = this.$refs['mc-table-fti'].selectedIds
      const ids = []

      Object.keys(selectedIds).forEach((id) => {
        if (selectedIds[id] == false) return

        ids.push(id)
      })

      if (ids.length == 0) {
        this.$mcToast.error(errorMsg['duplicate-qa/no-selected-data']())
        this.userGroupContext.model = {}
        this.skipValidationUserGroup = false
        return
      }

      if (!this.skipValidationUserGroup) {
        if (this.hasTmsUserGroupAccess) {
          this.$refs['mc-modal-user-group'].$refs['value-help-modal-ref'].show()
          return
        }
      }

      let data = { ids }
      if (this.hasTmsUserGroupAccess) {
        data = {
          ...data,
          user_group_id: Number(this.userGroupContext.model),
        }
      }

      const res = await FleetTaskInstantBff.duplicate(data)

      if (res.errors) {
        this.$mcToast.error(errorMsg['default']())
        this.userGroupContext.model = {}
        this.skipValidationUserGroup = false
        return
      }

      if (ids.length == 1 && res?.data?.failed_count === 0) {
        const query = this.$route.query
        this.$router.push({
          name: 'Fleet Task Instant Detail',
          params: { id: res.data.result?.[0].data.new_id },
          query,
        })

        this.$mcToast.success(successMsg['duplicate-qa/one']())
      } else {
        this.resetSelectedItems()
        if (res?.data?.success_count > 0) {
          this.tableKey += 1
        }

        const { success_count: totalSuccess, failed_count: totalFailed } = res.data

        if (totalFailed)
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: errorMsg['duplicate-qa/list'](totalFailed),
                },
              ],
            },
          })
        if (totalSuccess)
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: successMsg['duplicate-qa/list'](totalSuccess),
                },
              ],
            },
          })
      }
    },

    handleTransitionToScheduled() {
      const excludeStatus = difference(this.uniqueItemStatus, ['Draf'])

      // If user doesn't select any 'Draf' QA
      if (excludeStatus.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg['transition/to-scheduled'](),
              },
            ],
          },
        })

        return
      }

      this.$refs['mc-modal-scheduled'].show()
    },

    handleTransitionToStarted() {
      const excludeStatus = difference(this.uniqueItemStatus, ['Draf', 'Dijadwalkan'])

      // If user doesn't select any 'Draf' or 'Dijadwalkan' QA
      if (excludeStatus.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg['transition/to-started'](),
              },
            ],
          },
        })

        return
      }

      this.$refs['mc-modal-started'].show()
    },

    handleTransitionToEnded() {
      const excludeStatus = difference(this.uniqueItemStatus, ['Perjalanan'])

      // If user doesn't select any 'Perjalanan' QA
      if (excludeStatus.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg['transition/to-ended'](),
              },
            ],
          },
        })

        return
      }

      this.$refs['mc-modal-ended'].show()
    },

    handleTransitionToCanceled() {
      const excludeStatus = difference(this.uniqueItemStatus, ['Perjalanan'])

      // If user doesn't select any 'Perjalanan' QA
      if (excludeStatus.length > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg['transition/to-canceled'](),
              },
            ],
          },
        })

        return
      }

      this.$refs['mc-modal-canceled'].show()
    },

    async handleBulkTransition(data) {
      this.isTransitionLoading = true

      this.$refs['mc-modal-scheduled'].hide()
      this.$refs['mc-modal-started'].hide()
      this.$refs['mc-modal-ended'].hide()
      this.$refs['mc-modal-canceled'].hide()

      const transitionStatus = data.transition.status || {}
      const statusName =
        transitionStatus.name && statusNameToIdMap[transitionStatus.name]
          ? transitionStatus.name
          : statusLabelToNameMap[transitionStatus.label || transitionStatus.name]

      const ids = []
      this.selectedItems.forEach((item) => {
        if (!item) return

        ids.push(item.id)
      })

      let res

      if (this.isBulkTransitionV2) {
        res = await FleetTaskInstant.bulkTransitionV2({
          ids,
          status: statusNameToIdMap[statusName],
          fields: data.additionalPayload?.fields,
        })
      } else {
        res = await FleetTaskInstant.bulkTransition({
          ids,
          status: statusNameToIdMap[statusName],
          fields: data.additionalPayload?.fields,
        })
      }

      let successCount = this.isBulkTransitionV2
        ? res.data.succeeded_ids.length
        : res.data.success_count
      let errorCount = this.isBulkTransitionV2 ? res.data.failed_ids.length : res.data.failed_count

      if (successCount > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: `<strong>${successCount}</strong> ${successToastMsg[statusName]()}`,
              },
            ],
          },
        })
      }

      if (errorCount > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: `<strong>${errorCount}</strong> ${failedToastMsg[statusName]()}`,
              },
            ],
          },
        })
      }

      this.tableKey += 1
      this.resetSelectedItems()
      this.isTransitionLoading = false
    },

    handleDeleteSuccess(data) {
      if (data.success_count > 0) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: successMsg['delete-qa/list'](data.success_count),
              },
            ],
          },
        })
      }

      if (data.failed_count > 0) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: errorMsg['delete-qa/list'](data.failed_count),
              },
            ],
          },
        })
      }
    },

    handleUserGroupSelect(data) {
      this.userGroupContext.model = data
      this.skipValidationUserGroup = true

      this.$nextTick(() => {
        this.handleDuplicate()
      })
    },
  },
}
</script>

<style lang="scss">
@import '@/design/custom/pages/_fleettaskinstant.scss';

.mc-fleet-task-instant {
  .__status-dots {
    display: inline-block;
    margin: auto;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 8px;
  }

  .__point-line {
    width: 100%;
    height: 14px;
    border-radius: 999px;
  }

  .__point-line--legacy {
    flex-grow: 1;
    height: 4px;
    margin-right: 4px;
    border-radius: 2px;
  }

  .__point-line--legacy:last-child {
    margin-right: 0;
  }

  .__timeline-route {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .__timeline-route-counter {
    color: $gray-700;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
  }

  .__timeline-route-body {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 20px;
  }

  .__timeline-route-points {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .__timeline-route-point {
    flex: 1 1 0;
    margin-right: 4px;
  }

  .__timeline-route-point:last-child {
    margin-right: 0;
  }

  .__timeline-route-nav {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: 0;
    background: transparent;
    color: $gray-700;
    cursor: pointer;

    &:disabled {
      color: $gray-500;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  td {
    height: 59.8px;
  }

  .__header-note {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .text-truncate {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
